import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import { FadedOverlay } from '../../../components/FadedOverlay';
import windowService from '../../../services/windowService';
import { BrandCode, isLocked, type Session, type Volume } from '../../curriculum';

const LockedContentOverlayContent = styled.div`
  padding: ${Grid._4};
  text-align: center;

  h2,
  p,
  i {
    color: ${Type.Color.medium};
  }

  i {
    font-size: 48px;
  }
`;

const LockedContentOverlayTitle = styled.h2`
  margin: 16px 0 24px;
`;

const LockedContentOverlayDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;

  > p {
    margin: 0;

    &:last-of-type {
      max-width: 480px;
    }
  }

  button {
    margin-top: 24px;
  }
`;

type Data = {
  showOverlay: boolean;
  lockReason?: NonNullable<Session['lockReason']>;
  overlayProps: React.ComponentProps<typeof FadedOverlay>;
};

type Schedule = { volumes: Volume[]; sessions: Session[] };

type Props = {
  brandCode: BrandCode;
  curriculumId: string;
  isScheduleLoading: boolean;
  schedule?: Schedule;
};

type LockedContentMessageProps = {
  reason: NonNullable<Session['lockReason']>;
  brandCode: BrandCode;
  curriculumId: string;
};

const LockedContentMessage = ({ reason, brandCode, curriculumId }: LockedContentMessageProps) => {
  switch (reason) {
    case 'Trial':
      return (
        <>
          <LockedContentOverlayTitle>Unlock more content to reach the next generation</LockedContentOverlayTitle>
          <LockedContentOverlayDescription>
            <p>Your free trial comes to an end soon. Any sessions beyond this point are locked. </p>
            <p>
              Subscribe today for uninterrupted access to media-rich resources geared to engage the next generation with
              the Gospel.
            </p>
            <PrimaryButton
              onClick={() => windowService.redirectTo(`/subscribe-bible-study/${brandCode}/${curriculumId}`)}
            >
              View Subscription Options
            </PrimaryButton>
          </LockedContentOverlayDescription>
        </>
      );
    case 'SubscriptionSwitched':
      return (
        <>
          <LockedContentOverlayTitle>Your curriculum access is changing soon</LockedContentOverlayTitle>
          <LockedContentOverlayDescription>
            <p>Any sessions beyond this point are locked. </p>
            <p>
              Add your leaders to the right collection from the "people" page to prepare for this change and ensure they
              can access the new content.
            </p>
            <PrimaryButton onClick={() => windowService.redirectTo(`/org/purchases-and-subscriptions`)}>
              View Upcoming Change
            </PrimaryButton>
          </LockedContentOverlayDescription>
        </>
      );
    case 'SubscriptionExpiration':
      return null;
    // Currently the backend will send this lock reason when the subscription is coming up for renewal.
    // For now, we're going to continue to allow the customers to have access to their content beyond the
    // window of their license as we discuss how we want to handle this going forward.
    // return (
    //   <>
    //     <LockedContentOverlayTitle>Unlock more content to reach the next generation</LockedContentOverlayTitle>
    //     <LockedContentOverlayDescription>
    //       <p>Your subscription is ending soon. Any sessions beyond this point are locked. </p>
    //       <p>
    //         Resume your subscription for uninterrupted access so you can engage the next generation with the Gospel.
    //       </p>
    //       <PrimaryButton onClick={() => windowService.redirectTo(`/org/purchases-and-subscriptions`)}>
    //         View Subscription Options
    //       </PrimaryButton>
    //     </LockedContentOverlayDescription>
    //   </>
    // );
    default: {
      const _: never = reason;
      return null;
    }
  }
};

export const LockedContentOverlay = ({ brandCode, curriculumId, isScheduleLoading, schedule }: Props) => {
  const [data, setData] = useState<Data>({ showOverlay: false, overlayProps: { fadeHeight: 0, top: 0 } });
  const { showOverlay, lockReason, overlayProps } = data;

  useEffect(() => {
    if (isScheduleLoading || !schedule || schedule.sessions.length === 0) return;

    const firstLockedSession = schedule.sessions.find((s: Session) => isLocked(s));
    if (!firstLockedSession) return;

    const firstLockedSessionId = firstLockedSession.sessionId;

    setData(prev => ({ ...prev, lockReason: firstLockedSession.lockReason, showOverlay: true }));

    const resizeObserver = new ResizeObserver(() => {
      const lockedSessionElement = document.getElementById(firstLockedSessionId);

      setData(prev => ({
        ...prev,
        overlayProps: {
          fadeHeight: (lockedSessionElement?.offsetHeight || 0) * 1.35,
          top: lockedSessionElement?.offsetTop || 0,
        },
      }));
    });

    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, [isScheduleLoading, schedule]);

  if (!brandCode || !curriculumId || !showOverlay || !lockReason) return null;

  return (
    <FadedOverlay {...overlayProps}>
      <LockedContentOverlayContent>
        <i className="fas fa-lock" />
        <LockedContentMessage reason={lockReason} brandCode={brandCode} curriculumId={curriculumId} />
      </LockedContentOverlayContent>
    </FadedOverlay>
  );
};
